import React, { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Overlay,
  Dialog,
  Header,
  Title,
  Desc,
  Actions,
  Button,
  ApiError,
  ConfirmLabel,
  ConfirmInput,
} from "./styles";
import { useTaskStore } from "store/task";
import { ftpAPI } from "api";
import { openGlobalError } from "modal/error/errorModalStore";

const AddFtpUser = ({ open, setOpen }) => {
  const { addTask, tasks } = useTaskStore();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [apiErr, setApiErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setUsername("");
    setPassword("");
    setApiErr(null);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
    };
  }, [open]);

  const canSubmit = useMemo(() => username.trim().length > 0, [username]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const normalizedUsername = username.trim();
    const hasPendingConflict = tasks.some(
      (task) =>
        task.state === "pending" &&
        task.name?.trim() === normalizedUsername &&
        (task.job === "Create FTP User" || task.job === "Remove FTP User")
    );
    if (hasPendingConflict) {
      openGlobalError({
        title: "Task In Progress",
        message: "같은 FTP 사용자에 대한 작업이 아직 진행 중입니다.",
      });
      return;
    }
    if (password.length > 0 && password.length < 6) {
      openGlobalError({
        title: "Password Rule",
        message: "비밀번호는 최소 6자 이상이어야 합니다.",
      });
      return;
    }

    try {
      setApiErr(null);

      const data = await ftpAPI.createUser(normalizedUsername, password);
      addTask({
        name: normalizedUsername,
        id: data?.data.task_id,
        job: `Create FTP User`,
        state: "pending",
      });

      setOpen(false);
    } catch (err: any) {
      setApiErr(
        err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to add FTP user."
      );
    }
  };

  if (!open) return null;

  return createPortal(
    <Overlay>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-ftpuser-title"
      >
        <Header>
          <Title id="add-ftpuser-title">Add FTP User</Title>
        </Header>

        <Desc>새로운 FTP 계정을 추가합니다.</Desc>

        {apiErr && <ApiError role="alert">{apiErr}</ApiError>}

        <form
          onSubmit={handleSubmit}
          style={{
            display: "grid",
            gap: 14,
            marginTop: 12,
            paddingTop: 8,
          }}
        >
          <ConfirmLabel>Username *</ConfirmLabel>
          <ConfirmInput
            ref={inputRef}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="ex) user"
            inputMode="text"
            required
          />

          <ConfirmLabel>Password (optional)</ConfirmLabel>
          <ConfirmInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="password (min 6 chars)"
          />

          <Actions>
            <Button
              type="button"
              onClick={() => setOpen(false)}
              data-variant="ghost"
            >
              Cancel
            </Button>
            <Button type="submit" data-variant="primary" disabled={!canSubmit}>
              Add
            </Button>
          </Actions>
        </form>
      </Dialog>
    </Overlay>,
    document.body
  );
};

export default AddFtpUser;
